import ToggleableRow from '@/components/purchase/checkout/ToggleableRow';
import { useTranslation } from '@/i18n';
import type { AdditionalOfferItem } from '@/types/offer';
import type { CheckoutPassenger } from '@/types/passenger';
import { getPassengerName } from '@/utils/passenger';
import type { AncillaryValues, CheckoutValues } from '@/utils/zodSchema';
import { Checkbox, Typography } from '@fleet/webshop-ui';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

interface PassengerOptionsProps {
  ancillaryId: AdditionalOfferItem['id'];
  passengers: CheckoutValues['passengers'];
}

const PassengerOptions: FC<PassengerOptionsProps> = ({
  ancillaryId,
  passengers,
}) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext<AncillaryValues>();
  const selectedAncillaries = watch('selectedAncillaries');
  const currentAncillary = selectedAncillaries.find(
    (ancillary) => ancillary.id === ancillaryId
  );

  const handlePassengerToggle = useCallback(
    (passenger: CheckoutPassenger, checked: boolean) => {
      const allOtherSelectedAncillaries = selectedAncillaries.filter(
        (ancillary) => ancillary.id !== currentAncillary?.id
      );

      if (!currentAncillary) {
        return;
      }

      let updatedAncillary;

      if (checked) {
        updatedAncillary = {
          ...currentAncillary,
          passengersExternalReferences: [
            ...currentAncillary.passengersExternalReferences,
            passenger.externalReference,
          ],
        };
      } else {
        const newPassengersExternalReferences =
          currentAncillary.passengersExternalReferences.filter(
            (externalRef) => externalRef !== passenger.externalReference
          );

        if (newPassengersExternalReferences.length > 0) {
          updatedAncillary = {
            ...currentAncillary,
            passengersExternalReferences: [...newPassengersExternalReferences],
          };
        }
      }

      setValue('selectedAncillaries', [
        ...allOtherSelectedAncillaries,
        ...(updatedAncillary ? [updatedAncillary] : []),
      ]);
    },
    [currentAncillary, selectedAncillaries, setValue]
  );

  return (
    <div className="flex w-full flex-col gap-1 self-end pl-8">
      {passengers?.map((passenger, passengerIndex) => {
        const isActive = Boolean(
          currentAncillary?.passengersExternalReferences.includes(
            passenger.externalReference
          )
        );

        return (
          <ToggleableRow
            isActive={isActive}
            onClick={() => handlePassengerToggle(passenger, !isActive)}
            key={passenger.id}
            data-testid="ancillary-option-passenger"
          >
            <div className="flex w-full items-center gap-2">
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  isSelected={isActive}
                  onChange={(checked) =>
                    handlePassengerToggle(passenger, checked)
                  }
                  aria-label={t('text.selectPassenger')}
                  data-testid="passenger-option-toggle"
                />
              </div>
              <Typography variant="body2-bold">
                {getPassengerName(passenger, passengerIndex)}
              </Typography>
            </div>
          </ToggleableRow>
        );
      })}
    </div>
  );
};

export default PassengerOptions;
